/* ==========================================================================
   UG CART DRAWER — behaviour for the redesigned drawer.

   Everything is delegated from `document` because the theme replaces
   `.cart-drawer__inner` / the footer slot on every cart mutation
   (see CartDrawer._onCartChanged in theme.js).
   ========================================================================== */

(function () {
  if (window.ugCartInitialized) return;
  window.ugCartInitialized = true;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const recommendationLimit = 6;
  let recommendationRequest = 0;

  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  /**
   * Adds a variant and lets the theme re-render the drawer in place.
   * Using `variant:add` as baseEvent makes CartDrawer swap its content
   * immediately instead of waiting for the line-item loader animation.
   */
  async function addToCart(payload) {
    const sections = bundledSections();
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ sections: sections }, payload))
    });

    if (!response.ok) throw response;

    const cart = await response.json();

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } })
    );

    return cart;
  }

  function refreshDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function wait(delay) {
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  /* ---------------------------------------------------------------- gifts */

  async function onGiftClick(button) {
    if (button.classList.contains('is-loading')) return;

    const box = button.closest('.ugc-gift');
    const variantId = Number(button.dataset.freeGiftId);

    button.classList.add('is-loading');

    try {
      await addToCart({ id: variantId, quantity: 1, properties: { _free_gift: 'true' } });
      if (box) box.classList.add('is-removing');
      await wait(400);
    } catch (error) {
      console.log('UG cart: gift add failed', error);
      button.classList.remove('is-loading');
      if (box) box.classList.remove('is-removing');
      refreshDrawer();
    }
  }

  /* --------------------------------------------------------------- upsell */

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  function productLine(title) {
    return normalize(title).split(/[|–—-]/)[0].trim();
  }

  function productColor(title, tags) {
    const titleParts = normalize(title).split(/\s+[–—-]\s+/);
    if (titleParts.length > 1) return titleParts[titleParts.length - 1].trim();

    const knownColors = ['zwart', 'black', 'wit', 'white', 'wijnrood', 'bordeaux', 'rood', 'red', 'groen', 'green', 'bruin', 'brown', 'blauw', 'blue', 'paars', 'purple', 'grijs', 'grey', 'gray'];
    const normalizedTags = normalize(tags).split('|');
    return knownColors.find((color) => normalizedTags.includes(color)) || '';
  }

  function category(title, type) {
    const normalizedTitle = normalize(title);
    const normalizedType = normalize(type);
    if (/legging|tights/.test(normalizedTitle)) return 'legging';
    if (/short/.test(normalizedTitle)) return 'short';
    if (/crop|top|shirt|tank/.test(normalizedTitle)) return 'top';
    if (/bra|bh|sport.?bh/.test(normalizedTitle)) return 'bra';
    if (/string|thong|slip/.test(normalizedTitle)) return 'underwear';
    if (/sock|sok/.test(normalizedTitle)) return 'socks';
    if (/legging|tights/.test(normalizedType)) return 'legging';
    if (/short/.test(normalizedType)) return 'short';
    if (/bra|bh/.test(normalizedType)) return 'bra';
    if (/top|shirt/.test(normalizedType)) return 'top';
    return normalizedType || 'other';
  }

  function prefersBlackSocks(source) {
    const value = `${productColor(source.title, source.tags)} ${normalize(source.title)} ${normalize(source.tags)}`;
    return /zwart|black|donker\s*blauw|navy|donker\s*grijs|anthracite|antraciet|bruin|brown|bordeaux|wijnrood/.test(value);
  }

  function dispatchRecommendationEvent(action, card) {
    document.dispatchEvent(new CustomEvent('ug:cart-recommendation', {
      detail: {
        action: action,
        productId: card && card.dataset.productId,
        source: card && card.dataset.recommendationSource
      }
    }));
  }

  function selectMatchingSize(card, size) {
    if (!size) return false;
    const button = Array.from(card.querySelectorAll('.ugc-card__size')).find((candidate) => {
      return normalize(candidate.textContent) === normalize(size);
    });
    if (!button) {
      const sizeButtons = card.querySelectorAll('.ugc-card__size');
      if (sizeButtons.length) {
        sizeButtons.forEach((candidate) => candidate.setAttribute('aria-pressed', 'false'));
        const addButton = card.querySelector('button.ugc-card__add');
        if (addButton) {
          addButton.disabled = true;
          addButton.dataset.sizeRequired = 'true';
        }
      }
      return false;
    }

    if (button.disabled) {
      card.querySelectorAll('.ugc-card__size').forEach((candidate) => candidate.setAttribute('aria-pressed', 'false'));
      const addButton = card.querySelector('button.ugc-card__add');
      if (addButton) {
        addButton.disabled = true;
        addButton.dataset.sizeRequired = 'true';
      }
      return false;
    }

    selectSize(button);
    return true;
  }

  async function recommendationCards(source, intent, signal) {
    const params = new URLSearchParams({
      section_id: 'ug-cart-recommendations',
      product_id: source.productId,
      limit: '10',
      intent: intent
    });
    const response = await fetch(`${root()}recommendations/products?${params.toString()}`, { signal: signal });
    if (!response.ok) throw response;

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    return Array.from(documentFragment.querySelectorAll('.ugc-card')).map((card) => ({
      card: card,
      source: source,
      intent: intent
    }));
  }

  function scoreCandidate(candidate, cartCategories) {
    const card = candidate.card;
    const source = candidate.source;
    const candidateCategory = category(card.dataset.productTitle, card.dataset.productType);
    let score = candidate.intent === 'complementary' ? 2000 : candidate.intent === 'related' ? 1000 : 0;

    score += Math.max(0, 160 - source.index * 35);
    score += Math.max(0, 40 - Number(card.dataset.recommendationPosition || 10) * 3);

    if (productLine(card.dataset.productTitle) === productLine(source.title)) score += 140;

    const candidateColor = productColor(card.dataset.productTitle, card.dataset.productTags);
    const sourceColor = productColor(source.title, source.tags);
    if (candidateColor && candidateColor === sourceColor) score += 110;
    else if (candidateColor === 'zwart' || candidateColor === 'black') score += 20;

    if (!cartCategories.has(candidateCategory)) score += 170;
    else score -= 80;

    if (Array.from(card.querySelectorAll('.ugc-card__size')).some((button) => {
      return normalize(button.textContent) === normalize(source.size) && !button.disabled;
    })) score += 60;

    return score;
  }

  function renderRecommendations(box, candidates, sources, requestId) {
    if (requestId !== recommendationRequest || !box.isConnected) return;

    const rail = box.querySelector('.ugc-rail');
    const cartIds = new Set(sources.map((source) => String(source.productId)));
    const cartCategories = new Set(sources.map((source) => category(source.title, source.type)));
    const seen = new Set();
    const fallbackCandidates = Array.from(rail.querySelectorAll('.ugc-card')).map((card) => ({
      card: card,
      source: sources[0],
      intent: 'fallback'
    }));

    const ranked = candidates.concat(fallbackCandidates)
      .map((candidate) => {
        candidate.score = scoreCandidate(candidate, cartCategories);
        return candidate;
      })
      .sort((a, b) => b.score - a.score)
      .filter((candidate) => {
        const id = String(candidate.card.dataset.productId || '');
        if (!id || cartIds.has(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

    const pinnedCards = Array.from(box.parentElement.querySelectorAll('.ugc-pinned-candidates .ugc-card'));
    const pinned = pinnedCards.map((card) => ({
      card: card,
      source: sources[0],
      intent: 'pinned',
      score: 3000
    })).filter((candidate) => {
      const id = String(candidate.card.dataset.productId || '');
      return id && !cartIds.has(id);
    });
    const pinnedByRole = (role) => pinned.find((candidate) => candidate.card.dataset.pinnedRole === role);
    const selected = [];
    const selectedIds = new Set();
    const take = (candidate) => {
      if (!candidate || selected.length >= recommendationLimit) return false;
      const id = String(candidate.card.dataset.productId || '');
      if (!id || cartIds.has(id) || selectedIds.has(id)) return false;
      selectedIds.add(id);
      selected.push(candidate);
      return true;
    };
    const outfitCandidates = ranked.filter((candidate) => {
      const candidateCategory = category(candidate.card.dataset.productTitle, candidate.card.dataset.productType);
      return candidateCategory !== 'socks' && candidateCategory !== 'underwear';
    });

    take(outfitCandidates[0]);
    take(pinnedByRole('thong'));
    take(pinnedByRole(prefersBlackSocks(sources[0]) ? 'socks-black' : 'socks-white') ||
      pinnedByRole(prefersBlackSocks(sources[0]) ? 'socks-white' : 'socks-black'));
    take(outfitCandidates.find((candidate) => !selectedIds.has(String(candidate.card.dataset.productId || ''))));
    ranked.forEach(take);

    if (selected.length) {
      rail.replaceChildren(...selected.map((candidate) => {
        candidate.card.dataset.recommendationSource = candidate.intent;
        selectMatchingSize(candidate.card, candidate.source.size);
        dispatchRecommendationEvent('impression', candidate.card);
        return candidate.card;
      }));
    } else {
      Array.from(rail.querySelectorAll('.ugc-card')).slice(recommendationLimit).forEach((card) => card.remove());
      Array.from(rail.querySelectorAll('.ugc-card')).forEach((card) => dispatchRecommendationEvent('impression', card));
    }

    const count = rail.querySelectorAll('.ugc-card').length;
    box.classList.toggle('is-loading', count === 0);
    const nav = box.querySelector('.ugc-upsell__nav');
    if (nav) nav.hidden = count < 2;
  }

  async function hydrateRecommendations() {
    const box = document.querySelector('.cart-drawer [data-smart-recommendations]');
    if (!box || box.dataset.loading === 'true' || box.dataset.hydrated === 'true') return;

    const sources = Array.from(document.querySelectorAll('.cart-drawer .ugc-recommendation-sources [data-product-id]'))
      .map((node, index) => ({
        productId: node.dataset.productId,
        title: node.dataset.productTitle,
        type: node.dataset.productType,
        tags: node.dataset.productTags,
        size: node.dataset.selectedSize,
        index: index
      }));
    if (!sources.length) return;

    box.dataset.loading = 'true';
    const requestId = ++recommendationRequest;
    const controller = new AbortController();

    try {
      const responses = await Promise.allSettled(sources.flatMap((source) => [
        recommendationCards(source, 'complementary', controller.signal),
        recommendationCards(source, 'related', controller.signal)
      ]));
      const candidates = responses.flatMap((result) => result.status === 'fulfilled' ? result.value : []);

      renderRecommendations(box, candidates, sources, requestId);
    } catch (error) {
      if (error.name !== 'AbortError') renderRecommendations(box, [], sources, requestId);
    } finally {
      if (requestId === recommendationRequest && box.isConnected) {
        box.dataset.loading = 'false';
        box.dataset.hydrated = 'true';
      }
    }
  }

  function selectSize(button) {
    const card = button.closest('.ugc-card');
    if (!card) return;

    card.querySelectorAll('.ugc-card__size').forEach((other) => {
      other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
    });

    const addButton = card.querySelector('.ugc-card__add');
    if (addButton) {
      addButton.dataset.variantId = button.dataset.variantId;
      if (addButton.dataset.sizeRequired === 'true') {
        addButton.disabled = false;
        delete addButton.dataset.sizeRequired;
      }
    }

    const now = card.querySelector('.ugc-price__now');
    if (now && button.dataset.price) now.textContent = button.dataset.price;

    const was = card.querySelector('.ugc-price__was');
    if (was && button.dataset.compare) was.textContent = button.dataset.compare;
  }

  async function onAddClick(button) {
    if (button.disabled || button.classList.contains('is-loading') || button.classList.contains('is-in-cart')) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    button.classList.add('is-loading');

    try {
      await addToCart({ items: [{ id: variantId, quantity: 1 }] });
      dispatchRecommendationEvent('add', button.closest('.ugc-card'));
    } catch (error) {
      console.log('UG cart: upsell add failed', error);
      button.classList.remove('is-loading');
      refreshDrawer();
    }
  }

  function scrollRail(button) {
    const rail = button.closest('.ugc-upsell')?.querySelector('.ugc-rail');
    if (!rail) return;
    const direction = button.dataset.railDirection === 'prev' ? -1 : 1;
    rail.scrollBy({ left: direction * 147, behavior: 'smooth' });
  }

  /* ------------------------------------------------------------ delegation */

  document.addEventListener('click', function (event) {
    const gift = event.target.closest('[data-free-gift-id]');
    if (gift) {
      event.preventDefault();
      onGiftClick(gift);
      return;
    }

    const size = event.target.closest('.ugc-card__size');
    if (size) {
      event.preventDefault();
      selectSize(size);
      return;
    }

    const productLink = event.target.closest('.ugc-card a[href]');
    if (productLink) dispatchRecommendationEvent('click', productLink.closest('.ugc-card'));

    // The sold-out / multi-option variants of this class are a disabled button and a
    // plain link to the product page — neither should be hijacked.
    const add = event.target.closest('button.ugc-card__add');
    if (add) {
      event.preventDefault();
      onAddClick(add);
      return;
    }

    const rail = event.target.closest('[data-rail-direction]');
    if (rail) {
      event.preventDefault();
      scrollRail(rail);
    }
  });

  /**
   * The theme removes gifts again when the cart drops below their tier
   * (window.removeInvalidFreeGifts). It used to force a full page reload,
   * which closed the drawer — re-render the section instead.
   */
  document.addEventListener('free-gifts:removed', function () {
    refreshDrawer();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hydrateRecommendations);
  else hydrateRecommendations();

  if (window.MutationObserver) {
    new MutationObserver(function () {
      hydrateRecommendations();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
